import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../components/label/MoneyLabel';
import { DirectCostCenterIcon } from '../../components/icons/DirectCostCenterIcon';
import { IndirectCostCenterIcon } from '../../components/icons/IndirectCostCenterIcon';
import { TimeCostCenterIcon } from '../../components/icons/TimeCostCenterIcon';
import { numberToString } from '../../components/special/numberConverter';

const DetailRow = ({ label, children, color }) => (
  <Box className="flex justify-between items-center gap-2 py-0.5">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="caption"
      className="monospace"
      fontWeight="bold"
      sx={{ color: color || 'inherit' }}
    >
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" fontWeight="bold" className="mb-1">
    {children}
  </Typography>
);

const ProjectControllingDetails = ({ project }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const workProgress = project.workPackageProgress || 0;
  const plannedHours = Math.round(project.totalTimePlannedSeconds / 3600);
  const workedHours = Math.round(project.totalTimeWorkedSeconds / 3600);
  const openBillableHours = Math.round(
    project.openBillableTimeSeconds / 3600
  );

  const directCosts = (project.directCostCenters || []).reduce(
    (sum, cc) => sum + (cc.totalCost || 0),
    0
  );

  // Wirkliche Gemeinkosten: anteilig basierend auf Direktkosten des Projekts
  // share = Direktkosten Projekt / Gesamtkosten der Kostenstelle
  const allocatedIndirectCosts = (project.indirectCostCenters || []).reduce(
    (sum, cc) => {
      if (!cc.totalCost) return sum;
      const share = cc.totalCost > 0 ? directCosts / cc.totalCost : 0;
      return sum + cc.totalCost * Math.min(share, 1);
    },
    0
  );
  const totalCosts = directCosts + allocatedIndirectCosts;

  const hasOrder = project.orderedSumNetto > 0;
  const estimatedProfit = hasOrder
    ? project.orderedSumNetto - totalCosts
    : null;
  const actualProfit = project.invoicedSumNetto - totalCosts;
  const estimatedMargin =
    hasOrder && project.orderedSumNetto > 0
      ? (estimatedProfit / project.orderedSumNetto) * 100
      : null;
  const actualMargin =
    project.invoicedSumNetto > 0
      ? (actualProfit / project.invoicedSumNetto) * 100
      : null;

  const billingProgress = hasOrder
    ? (project.invoicedSumNetto / project.orderedSumNetto) * 100
    : 0;
  const progressDiff = workProgress - billingProgress;
  const billableFromProgress = hasOrder
    ? (progressDiff / 100) * project.orderedSumNetto
    : 0;

  const remaining = hasOrder
    ? Math.max(0, project.orderedSumNetto - project.invoicedSumNetto)
    : 0;

  const profitColor = (val) =>
    val > 0
      ? colors.money.positive
      : val < 0
        ? colors.money.negative
        : colors.money.neutral;

  const allCostCenters = [
    ...(project.directCostCenters || []),
    ...(project.indirectCostCenters || []),
  ];

  return (
    <Box>
      {/* 3-column grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Column 1: Progress */}
        <Box sx={{ backgroundColor: colors.card, borderRadius: '8px', p: 2 }}>
          <SectionTitle>{t('Time Billing')}</SectionTitle>
          <DetailRow label={t('Work Progress')}>
            {numberToString(workProgress, 1)} %
          </DetailRow>
          <DetailRow label={t('Time Planned')}>{plannedHours} h</DetailRow>
          <DetailRow label={t('Time Worked')}>{workedHours} h</DetailRow>
          <DetailRow label={t('Open Billable Hours')}>
            {openBillableHours} h
          </DetailRow>
          <DetailRow
            label={t('Open Billable Amount')}
            color={
              project.openBillableTimeNetto > 0
                ? colors.money.positive
                : undefined
            }
          >
            <MoneyLabel money={project.openBillableTimeNetto} />
          </DetailRow>

          <Box sx={{ borderTop: `1px solid ${colors.glass}`, mt: 1, pt: 1 }}>
            <SectionTitle>{t('Performance Billing')}</SectionTitle>
            <DetailRow label={t('Work Progress')}>
              {numberToString(workProgress, 1)} %
            </DetailRow>
            <DetailRow label={t('Billing Status')}>
              {hasOrder ? numberToString(billingProgress, 1) + ' %' : '–'}
            </DetailRow>
            <DetailRow label={t('Difference')}>
              {hasOrder ? numberToString(progressDiff, 1) + ' %' : '–'}
            </DetailRow>
            <DetailRow
              label={t('Billable Amount')}
              color={
                hasOrder && billableFromProgress > 0
                  ? colors.money.positive
                  : hasOrder && billableFromProgress < 0
                    ? colors.money.negative
                    : undefined
              }
            >
              {hasOrder ? <MoneyLabel money={billableFromProgress} /> : '–'}
            </DetailRow>
          </Box>
        </Box>

        {/* Column 2: Financials */}
        <Box sx={{ backgroundColor: colors.card, borderRadius: '8px', p: 2 }}>
          <SectionTitle>{t('Financial Summary')}</SectionTitle>
          <DetailRow label={t('Order Volume')}>
            <MoneyLabel money={project.orderedSumNetto} />
          </DetailRow>
          <DetailRow label={t('Invoiced')}>
            <MoneyLabel money={project.invoicedSumNetto} />
          </DetailRow>
          <DetailRow label={t('Remaining Volume')} color={profitColor(remaining)}>
            {hasOrder ? <MoneyLabel money={remaining} /> : '–'}
          </DetailRow>
        </Box>

        {/* Column 3: Profit Analysis */}
        <Box sx={{ backgroundColor: colors.card, borderRadius: '8px', p: 2 }}>
          <SectionTitle>{t('Profit Analysis')}</SectionTitle>
          <DetailRow label={t('Total Costs')}>
            <MoneyLabel money={totalCosts} />
          </DetailRow>
          <DetailRow
            label={t('Estimated Profit')}
            color={estimatedProfit != null ? profitColor(estimatedProfit) : undefined}
          >
            {estimatedProfit != null ? (
              <MoneyLabel money={estimatedProfit} />
            ) : (
              '–'
            )}
          </DetailRow>
          <DetailRow
            label={t('Actual Profit')}
            color={profitColor(actualProfit)}
          >
            <MoneyLabel money={actualProfit} />
          </DetailRow>
          <DetailRow label={t('Estimated Margin')}>
            {estimatedMargin != null
              ? numberToString(estimatedMargin, 1) + ' %'
              : '–'}
          </DetailRow>
          <DetailRow label={t('Actual Margin')}>
            {actualMargin != null
              ? numberToString(actualMargin, 1) + ' %'
              : '–'}
          </DetailRow>
        </Box>
      </Box>

      {/* Cost Centers */}
      {allCostCenters.length > 0 && (
        <Box className="mt-3" sx={{ backgroundColor: colors.card, borderRadius: '8px', p: 2 }}>
          <SectionTitle>{t('Cost Centers')}</SectionTitle>
          <Box className="flex flex-col gap-1">
            {allCostCenters.map((cc) => {
              const isIndirect = cc.type === 'INDIRECT_COSTS';
              const share = isIndirect && cc.totalCost > 0
                ? Math.min(directCosts / cc.totalCost, 1)
                : 1;
              const displayCost = (cc.totalCost || 0) * share;

              return (
                <Box key={cc.id} className="flex items-center gap-2 py-0.5">
                  <Box sx={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {cc.type === 'DIRECT_COSTS' && <DirectCostCenterIcon size="20px" />}
                    {cc.type === 'INDIRECT_COSTS' && <IndirectCostCenterIcon size="20px" />}
                    {cc.type === 'TIME_COSTS' && <TimeCostCenterIcon size="20px" />}
                  </Box>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {cc.name}
                    {isIndirect && (
                      <Typography component="span" variant="caption" sx={{ opacity: 0.5, ml: 0.5 }}>
                        ({numberToString(share * 100, 1)} %)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" className="monospace" fontWeight="bold">
                    <MoneyLabel money={displayCost} />
                  </Typography>
                </Box>
              );
            })}
            <Box className="flex items-center gap-2 pt-1 mt-1" sx={{ borderTop: `1px solid ${colors.glass}` }}>
              <Box sx={{ width: 24, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ flex: 1 }} fontWeight="bold">
                {t('Total Costs')}
              </Typography>
              <Typography variant="caption" className="monospace" fontWeight="bold">
                <MoneyLabel money={totalCosts} />
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectControllingDetails;
